import React, { useState } from 'react';
import { ExternalLink, Smartphone, Globe, Users, ChevronRight } from 'lucide-react';
import { projectsData } from '../data/mockData';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

const Projects = () => {
  const [showAll, setShowAll] = useState(false);
  const displayedProjects = showAll ? projectsData : projectsData.filter(p => p.featured);

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Fintech':
        return '💳';
      case 'Payment Gateway':
        return '💰';
      case 'Ride Sharing':
        return '🚗';
      case 'Full Stack':
        return '🌐';
      case 'EdTech':
        return '📚';
      default:
        return '📱';
    }
  };

  return (
    <section id="projects" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-emerald-600 font-medium text-sm tracking-wide uppercase">
            Portfolio
          </span>
          <h2 className="text-3xl sm:text-4xl font-light text-gray-900 mt-2">
            Featured{' '}
            <span className="font-semibold">Projects</span>
          </h2>
          <p className="text-gray-500 mt-4 max-w-2xl mx-auto">
            A selection of projects I've worked on, ranging from fintech applications 
            with millions of users to innovative ride-sharing platforms.
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayedProjects.map((project) => (
            <div
              key={project.id}
              className="group bg-white rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-100"
            >
              {/* Project Header */}
              <div className="p-6 pb-4">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">{getCategoryIcon(project.category)}</span>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 group-hover:text-emerald-600 transition-colors">
                        {project.name}
                      </h3>
                      <span className="text-sm text-gray-500">{project.category}</span>
                    </div>
                  </div>
                  {project.users && (
                    <div className="flex items-center text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
                      <Users className="w-3 h-3 mr-1" />
                      <span className="text-xs font-medium">{project.users}</span>
                    </div>
                  )}
                </div>

                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  {project.description}
                </p>

                {/* Role */}
                <div className="inline-flex items-center text-sm text-gray-700 mb-4">
                  <span className="font-medium">Role:</span>
                  <span className="ml-2 text-gray-600">{project.role}</span>
                </div>

                {/* Tech Stack */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.techStack.slice(0, 3).map((tech, i) => (
                    <Badge
                      key={i}
                      variant="outline"
                      className="text-xs font-normal"
                    >
                      {tech}
                    </Badge>
                  ))}
                  {project.techStack.length > 3 && (
                    <Badge variant="outline" className="text-xs font-normal">
                      +{project.techStack.length - 3}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Project Links */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                <div className="flex flex-wrap gap-3">
                  {project.links.android && (
                    <a
                      href={project.links.android}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm text-gray-600 hover:text-emerald-600 transition-colors"
                    >
                      <Smartphone className="w-4 h-4 mr-1" />
                      Android
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </a>
                  )}
                  {project.links.ios && (
                    <a
                      href={project.links.ios}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm text-gray-600 hover:text-emerald-600 transition-colors"
                    >
                      <Smartphone className="w-4 h-4 mr-1" />
                      iOS
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </a>
                  )}
                  {project.links.web && (
                    <a
                      href={project.links.web}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm text-gray-600 hover:text-emerald-600 transition-colors"
                    >
                      <Globe className="w-4 h-4 mr-1" />
                      Website
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Show More Button */}
        {!showAll && projectsData.length > 6 && (
          <div className="text-center mt-12">
            <Button
              variant="outline"
              size="lg"
              onClick={() => setShowAll(true)}
              className="px-8"
            >
              View All Projects
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Projects;
